import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Check, FileText, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ClientCompanyPerson,
  IncidentReportAvailability,
  IncidentReportSendResult,
  useClientCompany,
  useIncidentReportAvailability,
  useIncidentReportDraft,
  useIncidentReportPdfUrl,
  useListClientCompanies,
  useListSprints,
  useProductManagerRecipients,
  useRefreshIncidentReport,
  useSendIncidentReports,
} from '@data';
import { Loader, LoaderSize } from '@ui';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { MultiSelectField, MultiSelectOption } from '@ui/components/ui/multi-select';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Skeleton } from '@ui/components/ui/skeleton';
import {
  ApprovalRecipientsField,
  type ApprovalRecipientsFieldHandle,
  type FormOptionType,
} from '@app/components/core/approvalPopup/ApprovalRecipientsField';
import { bySprintRecency, dayKey, formatDateString, fullDate } from '@app/utils';
import { Language } from '@app/constants/enums';
import { MailPreview } from '@app/components/shared/mailPreview';
import IncidentReportEntriesPanel, { type EntriesFile } from '../../incidentReportEntries';

import { closedSprintsOf } from './closedSprints';

// Who the last send was addressed to, remembered per browser: a convenience, so it is read defensively.
const lastRecipientsKey = (clientId: string): string => `phill:incidentReportRecipients:${clientId}`;

const loadLastReportRecipients = (clientId: string): string[] => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(lastRecipientsKey(clientId)) ?? '[]');

    return Array.isArray(stored) ? stored.filter((email): email is string => typeof email === 'string') : [];
  } catch {
    return [];
  }
};

const saveLastReportRecipients = (clientId: string, emails: string[]): void => {
  try {
    localStorage.setItem(lastRecipientsKey(clientId), JSON.stringify(emails));
  } catch {
    // A browser with site data switched off still sends the report; it just forgets who it went to.
  }
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

type FileKey = string;
const fileKey = (productId: string, sprintId: string): FileKey => `${productId}:${sprintId}`;

/** How far back the sprint picker looks: a support report older than this is never the one being sent. */
const REPORTABLE_YEARS = 2;

/** What the availability endpoint accepts at once (`ArrayMaxSize(50)`); more than this is refused outright. */
const AVAILABILITY_SPRINT_LIMIT = 50;

/** How long the entries panel takes to leave. The same 300ms it arrives in, and `duration-300` on it. */
const ENTRIES_LEAVE_MS = 300;

/** One report's PDF. All are mounted at once and hidden by opacity, not display: a frame with no box refits and jumps when shown. */
const ReportFrame = ({
  productId,
  sprintId,
  filename,
  active,
  clickThrough,
  onReady,
}: {
  productId: string;
  sprintId: string;
  filename: string;
  active: boolean;
  /** Whether a click should reach the page instead of the viewer, so an open picker can be dismissed on it. */
  clickThrough: boolean;
  /** Told once this frame has a page worth showing, so the window can wait for it. */
  onReady: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useIncidentReportPdfUrl(productId, sprintId);

  // Held covered for a beat after load: the viewer paints its chrome first and fits the page a moment later.
  const [painted, setPainted] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const onLoad = (): void => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPainted(true);
      onReady();
    }, 800);
  };

  return (
    <div
      aria-hidden={!active}
      className={cx(
        'absolute inset-0 overflow-hidden bg-white transition-opacity duration-150',
        active ? 'z-10 opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      {/*
       Toolbar and rail off, and widened past the box to crop the viewer's own dark gutter. Sideways
       only: cropping the top ate the head of the document's band. */}
      {data && (
        <iframe
          src={`${data.url}#toolbar=0&navpanes=0&statusbar=0&view=FitH`}
          title={filename}
          onLoad={onLoad}
          className={cx(
            '-ml-6 h-full w-[calc(100%+48px)] border-0 transition-opacity duration-200',
            painted ? 'opacity-100' : 'opacity-0',
            // A click inside the viewer is the iframe's own; the page never hears it, so the picker stays open.
            clickThrough && 'pointer-events-none',
          )}
        />
      )}

      {(isLoading || (!!data && !painted)) && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}

      {isError && <p className="p-6 text-sm text-muted-foreground">{t('incidentReports.previewFailed')}</p>}
    </div>
  );
};

// The report's grid in points: the PDF draws A4 at a 21pt row pitch, the headings' rule 95.95pt down.
const REPORT_PAGE_WIDTH = 595.28;
const REPORT_ROW_PITCH = 21;
const REPORT_FIRST_ROW = 95.95;

/** Export a client's incident reports on one screen. One PDF per product and sprint, one mail per recipient. */
const IncidentReportExportDialog = ({ open, onOpenChange, organizationId }: Props): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [chosenClient, setChosenClient] = useState<string>();
  const [chosenProducts, setChosenProducts] = useState<string[]>([]);
  const [chosenSprints, setChosenSprints] = useState<string[]>([]);
  // One list, as the sprint report's send dialog holds it: the picker speaks only for the client's contacts.
  const [to, setTo] = useState<FormOptionType[]>([]);
  const toField = useRef<ApprovalRecipientsFieldHandle>(null);
  const [activeFile, setActiveFile] = useState<FileKey | null>(null);
  // Two steps: the choosing screen is already four pickers, and the mail is the part that is written.
  const [step, setStep] = useState<'choose' | 'compose'>('choose');
  /** Which document's entries are being corrected. Declared with the steps, since one reset clears both. */
  const [editing, setEditing] = useState<FileKey | null>(null);
  /** The panel on its way out, still there while it plays the arrival backwards. */
  const [leavingEntries, setLeavingEntries] = useState(false);
  /** Next, pressed while the mail was still being drafted: the step follows once it is there. */
  const [awaited, setAwaited] = useState(false);
  /** Whether the recipients list is showing, so a click in the mail under it can put it away. */
  const [pickingRecipients, setPickingRecipients] = useState(false);
  // The slide is for a step that came out of another: the first showing came out of nothing.
  const [moved, setMoved] = useState(false);
  const goTo = (next: 'choose' | 'compose'): void => {
    setMoved(true);
    setStep(next);
  };

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  /** Which selection the two above were filled from, so a new one prefills and an edit is not undone. */
  const seeded = useRef<string | null>(null);
  // The window waits on the active frame rather than a timer, so it cannot reveal a half-laid-out page.
  const [readyFiles, setReadyFiles] = useState<Set<FileKey>>(new Set());
  const [result, setResult] = useState<IncidentReportSendResult | null>(null);

  const { data: companyData } = useListClientCompanies(open ? organizationId : undefined);
  const companies = useMemo(() => companyData?.items ?? [], [companyData]);

  // Read singly: the list leaves `people` out, and the recipients are the whole point of this dialog.
  const { data: company } = useClientCompany(organizationId, chosenClient || undefined);
  const products = useMemo(() => (company?.products ?? []).map(({ id, name }) => ({ id, name })), [company]);

  // Everyone registered onto this client; their own access decides nothing, since we send the report.
  const people: ClientCompanyPerson[] = useMemo(() => company?.people ?? [], [company]);

  // Managers from the API, not the products list, whose display names cannot address a mail.
  const { data: managerData } = useProductManagerRecipients(organizationId, chosenProducts);
  const managers = useMemo(() => managerData?.items ?? [], [managerData]);

  const { data: sprintData } = useListSprints();
  const sprints = useMemo(() => [...(sprintData?.items ?? [])].sort(bySprintRecency), [sprintData]);
  // The span the availability check is asked about: older sprints are never sent, and widen its query for nothing.
  const candidateSprints = useMemo(() => {
    const earliest = new Date();
    earliest.setFullYear(earliest.getFullYear() - REPORTABLE_YEARS);
    const cutoff = dayKey(earliest);

    // Newest first already, so the cap drops the oldest — the ones least likely to be the send.
    return closedSprintsOf(sprints)
      .filter(sprint => fullDate(sprint.endDate) >= cutoff)
      .slice(0, AVAILABILITY_SPRINT_LIMIT);
  }, [sprints]);
  const candidateSprintIds = useMemo(() => candidateSprints.map(sprint => sprint.id), [candidateSprints]);
  const sprintDefaulted = useRef(false);
  const productsDefaultedFor = useRef<string | null>(null);
  /** Which picker is open, if any: the PDF preview has to stop swallowing the click that should close it. */
  const [openPicker, setOpenPicker] = useState<'client' | 'sprints' | 'products' | null>(null);

  // Every opening starts clean, back at whichever client the caller named.
  useEffect(() => {
    if (!open) return;
    setChosenClient(undefined);
    setChosenSprints([]);
    setOpenPicker(null);
    sprintDefaulted.current = false;
    productsDefaultedFor.current = null;
    setActiveFile(null);
    setEditing(null);
    setLeavingEntries(false);
    setStep('choose');
    setMoved(false);
    setSubject('');
    setMessage('');
    // Forgotten with the words: the dialog never unmounts, so a stale selection stopped the draft reseeding.
    seeded.current = null;
    setReadyFiles(new Set());
    setResult(null);
  }, [open]);

  // A different client has different products and different sprints, so both choices start again with it.
  useEffect(() => {
    if (!open) return;
    sprintDefaulted.current = false;
    productsDefaultedFor.current = null;
    setChosenSprints([]);
    setChosenProducts([]);
  }, [open, chosenClient]);

  // Our own people are copied in, not addressed, and the chip says so.
  const isCopied = (email: string): boolean => {
    const lower = email.toLowerCase();

    return (
      !people.some(person => person.email.toLowerCase() === lower) &&
      managers.some(manager => manager.email.toLowerCase() === lower)
    );
  };
  // Whoever was ticked last, not whoever sits first; a copied-in manager is nobody's addressee.
  const [lastAddressee, setLastAddressee] = useState<string>();
  const noteAdditions = (next: FormOptionType[]): void => {
    const added = next.filter(entry => !to.some(held => held.value === entry.value)).map(entry => entry.value);
    const addressed = added.filter(email => !isCopied(email));
    if (addressed.length) setLastAddressee(addressed[addressed.length - 1]);
  };

  const chipFor = (email: string): FormOptionType => ({
    value: email,
    label: isCopied(email) ? `${email} ${t('incidentReports.ccSuffix')}` : email,
    isFixed: false,
  });

  // Recipients follow the chosen client, which is unknown on the first render.
  useEffect(() => {
    // Addressed as the last send was, kept even if that person is no longer a contact: it was deliberate.
    const remembered = company ? loadLastReportRecipients(company.id) : [];
    const emails = remembered.length ? remembered : people.map(person => person.email);
    setTo(emails.map(chipFor));
    setLastAddressee(undefined);
  }, [people, company]);

  const productIds = useMemo(() => products.map(product => product.id), [products]);

  // Asked about every product the client has, not the chosen ones: the sprint is picked first, so they are the answer.
  const { data: availability, isFetching: checking } = useIncidentReportAvailability(
    organizationId,
    productIds,
    candidateSprintIds,
  );

  const availabilityByKey = useMemo(() => {
    const map = new Map<FileKey, IncidentReportAvailability>();
    (availability?.items ?? []).forEach(entry => map.set(fileKey(entry.productId, entry.sprintId), entry));

    return map;
  }, [availability]);

  // Only sprints one of the client's products worked an incident in: the rest would send nothing.
  const reportableSprints = useMemo(
    () =>
      candidateSprints.filter(sprint =>
        productIds.some(productId => availabilityByKey.get(fileKey(productId, sprint.id))?.available),
      ),
    [candidateSprints, productIds, availabilityByKey],
  );

  // Claimed once the availability answer is in, or at once when there is nothing to ask it about.
  const nothingToReport =
    !!company &&
    !checking &&
    (!productIds.length || !candidateSprintIds.length || (!!availability && !reportableSprints.length));

  // The most recent one starts chosen, once per opening, so a refetch cannot undo a deselection.
  useEffect(() => {
    if (!open || sprintDefaulted.current || !reportableSprints.length) return;
    sprintDefaulted.current = true;
    setChosenSprints([reportableSprints[0].id]);
  }, [open, reportableSprints]);

  // Keyed on the sprints themselves, so changing them re-picks but a refetch cannot undo a deselection.
  useEffect(() => {
    if (!open || !availability) return;
    const key = [...chosenSprints].sort().join(',');
    if (productsDefaultedFor.current === key) return;
    productsDefaultedFor.current = key;
    setChosenProducts(
      productIds.filter(productId =>
        chosenSprints.some(sprintId => availabilityByKey.get(fileKey(productId, sprintId))?.available),
      ),
    );
  }, [open, availability, chosenSprints, productIds, availabilityByKey]);

  /** The files this selection produces: products outer, sprints inner, matching the send's own order. */
  const files = useMemo(
    () =>
      chosenProducts.flatMap(productId =>
        chosenSprints
          .filter(sprintId => availabilityByKey.get(fileKey(productId, sprintId))?.available)
          .map(sprintId => ({
            key: fileKey(productId, sprintId),
            productId,
            sprintId,
            productName: products.find(product => product.id === productId)?.name ?? '',
            sprintNumber: sprints.find(sprint => sprint.id === sprintId)?.number ?? 0,
          })),
      ),
    [chosenProducts, chosenSprints, availabilityByKey, products, sprints],
  );

  const period = (from?: string, to?: string): string =>
    from && to
      ? `${formatDateString(from, language as Language, 'dd MMM')} – ${formatDateString(to, language as Language, 'dd MMM yyyy')}`
      : '';

  const clientOptions: MultiSelectOption[] = useMemo(
    () =>
      companies.map(item => ({
        value: item.id,
        label: item.name,
        tag: t('incidentReports.productCount', { count: item.products.length }),
      })),
    [companies, t],
  );

  /** A pill where a product yields nothing: four chosen and three documents otherwise reads as a bug. */
  const productOptions: MultiSelectOption[] = useMemo(
    () =>
      products.map(product => {
        const entries = chosenSprints.map(sprintId => availabilityByKey.get(fileKey(product.id, sprintId)));
        const yields = !chosenSprints.length || checking || entries.some(entry => entry?.available);
        const unfinished = entries.length > 0 && entries.every(entry => entry?.reason === 'notReportable');

        return {
          value: product.id,
          label: product.name,
          ...(yields
            ? {}
            : {
                tag: unfinished ? t('incidentReports.sprintStillOpen') : t('incidentReports.nothingToReport'),
                tagTone: 'warning' as const,
              }),
        };
      }),
    [products, chosenSprints, availabilityByKey, checking, t],
  );

  const sprintOptions: MultiSelectOption[] = useMemo(
    () =>
      reportableSprints.map(sprint => ({
        value: sprint.id,
        label: `${t('sprint')} ${sprint.number}`,
        description: period(sprint.startDate as unknown as string, sprint.endDate as unknown as string),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportableSprints, language, t],
  );

  const personLabel = (person: ClientCompanyPerson): string =>
    [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email;

  // Both groups in one list; somebody in both appears once, under the client heading.
  const recipientOptions: MultiSelectOption[] = useMemo(() => {
    const clientRows = people.map(person => ({
      value: person.email,
      label: personLabel(person),
      description: person.email,
    }));
    const seen = new Set(people.map(person => person.email.toLowerCase()));
    const managerRows = managers
      .filter(manager => !seen.has(manager.email.toLowerCase()))
      .map(manager => ({
        value: manager.email,
        label: manager.name,
        description: manager.email,
        // The chip says (Cc) once picked; the row says it before, which is when it is decided.
        tags: [{ text: t('mailPreview.ccTag') }],
      }));

    return [...clientRows, ...managerRows];
  }, [people, managers, t]);

  // Every address the picker offers, client contact or product manager alike.
  const offeredEmails = useMemo(
    () => new Set(recipientOptions.map(option => option.value.toLowerCase())),
    [recipientOptions],
  );
  const chosenOffered = useMemo(
    () => to.filter(entry => offeredEmails.has(entry.value.toLowerCase())).map(entry => entry.value),
    [to, offeredEmails],
  );

  /** Reordered rather than appended, so putting a contact back does not land them after a typed address. */
  const onContactsChange = (nextEmails: string[]): void => {
    const typedIn = to.filter(entry => !offeredEmails.has(entry.value.toLowerCase()));
    const next = [...nextEmails.map(chipFor), ...typedIn];

    noteAdditions(next);
    setTo(next);
  };

  /** For the mail's greeting, from whichever group the address came from. */
  const firstNameOf = (email: string): string | undefined => {
    const lower = email.toLowerCase();

    return (
      people.find(person => person.email.toLowerCase() === lower)?.firstName ??
      managers.find(manager => manager.email.toLowerCase() === lower)?.firstName
    );
  };

  const recipients = useMemo(
    () => to.map(entry => ({ email: entry.value, firstName: firstNameOf(entry.value), cc: isCopied(entry.value) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [to, people, managers],
  );

  // Labelled here: managers arrive after the box is filled, so a chip built then kept a bare address.
  const toChips = useMemo(
    () => to.map(entry => chipFor(entry.value)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [to, people, managers, t],
  );

  // A detour rather than a step: it covers the batch while open and gives it straight back.
  const refreshReport = useRefreshIncidentReport();

  // Held here, not in the panel: what outlasts the animation is what decides the panel exists.
  const leaving = useRef<number>();
  const closeEntries = (): void => {
    if (leaving.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setEditing(null);

      return;
    }
    setLeavingEntries(true);
    leaving.current = window.setTimeout(() => {
      leaving.current = undefined;
      setLeavingEntries(false);
      setEditing(null);
    }, ENTRIES_LEAVE_MS);
  };
  useEffect(
    () => () => {
      window.clearTimeout(leaving.current);
    },
    [],
  );

  // A refetch can take the corrected document out of the batch from under the panel.
  useEffect(() => {
    if (editing && !files.some(file => file.key === editing)) setEditing(null);
  }, [editing, files]);

  // Everyone chosen is one of ours, so the send falls back to addressing them and the client hears nothing.
  const noClientAddressed = to.length > 0 && to.every(entry => isCopied(entry.value));

  // Addressees only: a copied-in manager is nobody's addressee, and more than one name makes it a merge field.
  const greetedNames = useMemo(() => {
    const addressed = to.filter(entry => !isCopied(entry.value));
    const entries = addressed.length ? addressed : to;
    // The one picked last comes first: that is the copy the greeting stands for.
    const newest = entries.filter(entry => entry.value === lastAddressee);
    const ordered = [...newest, ...entries.filter(entry => entry.value !== lastAddressee)];

    return [...new Set(ordered.map(entry => firstNameOf(entry.value)))].filter((name): name is string => !!name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, people, managers, lastAddressee]);

  const selectionKey = `${[...chosenProducts].sort().join()}|${[...chosenSprints].sort().join()}`;
  // Asked for as soon as there is a batch, and seeded once per selection, so an edit survives a trip back.
  const { data: draft, isFetching: drafting } = useIncidentReportDraft(
    organizationId,
    chosenProducts,
    chosenSprints,
    files.length > 0,
    // The first of them, which is the copy the preview renders. See `greetedNames`.
    greetedNames[0],
  );
  useEffect(() => {
    if (!draft || seeded.current === selectionKey) return;
    seeded.current = selectionKey;
    setSubject(draft.subject);
    setMessage(draft.message);
  }, [draft, selectionKey]);
  // The step that was asked for while the draft was still coming.
  useEffect(() => {
    if (!awaited || !draft) return;
    setAwaited(false);
    goTo('compose');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaited, draft]);
  // A draft that never arrives must not leave the button spinning: the step says so itself.
  useEffect(() => {
    if (!awaited || drafting) return undefined;
    const giveUp = setTimeout(() => {
      setAwaited(false);
      goTo('compose');
    }, 4000);

    return () => clearTimeout(giveUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaited, drafting]);

  const send = useSendIncidentReports(organizationId);

  const active = files.find(file => file.key === activeFile) ?? files[0];

  // A tab names its own sprint, which is what let the row of sprint pills above it go.
  const tabLabel = (file: { productName: string; sprintNumber: number }): string =>
    `${file.productName} - ${t('sprint')} ${file.sprintNumber}`;

  // Mirrors `incidentReportFilename` on purpose: the window claims to show the file that gets sent.
  const filenameOf = (file: { productName: string; sprintNumber: number }): string =>
    `${file.productName} - Incident Report - Sprint ${file.sprintNumber}.pdf`;

  // The entries know the documents by the names the tab strip gives them, so both call them the same.
  const entriesFiles = useMemo<EntriesFile[]>(
    () =>
      files.map(file => ({ key: file.key, productId: file.productId, sprintId: file.sprintId, label: tabLabel(file) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, t],
  );

  const submit = async (): Promise<void> => {
    // A typed, uncommitted address is still meant, so it is flushed rather than dropped.
    const flushed = toField.current?.commitDraft();
    if (flushed === null) return;

    const list = (flushed ?? to).map(entry => ({
      email: entry.value,
      firstName: firstNameOf(entry.value),
      cc: isCopied(entry.value),
    }));
    const addressed = list.map(entry => entry.email);
    const outcome = await send.mutateAsync({
      productIds: chosenProducts,
      sprintIds: chosenSprints,
      recipients: list,
      subject,
      message,
    });
    // Remembered for the next send to this client, addresses typed in by hand included.
    if (company) saveLastReportRecipients(company.id, addressed);
    setResult(outcome);
  };

  /** Why there is no document: an unfinished sprint is told apart from one with no support work. */
  const emptyPreview = ((): string => {
    if (!company) return t('incidentReports.pickClient');
    if (nothingToReport) return t('incidentReports.noSprintsToReport');
    if (!chosenSprints.length) return t('incidentReports.pickSprints');
    if (!chosenProducts.length) return t('incidentReports.pickProducts');
    // Files exist and the document behind them is still settling, which is what the reveal waits on.
    if (checking || files.length > 0) return t('incidentReports.checking');

    const reasons = (availability?.items ?? [])
      .filter(entry => chosenSprints.includes(entry.sprintId) && !entry.available)
      .map(entry => entry.reason);
    if (reasons.length > 0 && reasons.every(reason => reason === 'notReportable')) {
      return t('incidentReports.sprintNotClosed');
    }

    return t('incidentReports.nothingForSelection');
  })();

  /** A batch to show at all, which is what the window is for; its documents come after. */
  const hasFiles = files.length > 0;
  // Shown once the document on top of it is ready; until then the placeholder holds the space.
  const showWindow = !!active && readyFiles.has(active.key);

  // How much of a row the bottom edge lands in, measured from the pane, whose width is the scale.
  const [sliver, setSliver] = useState(0);
  const watching = useRef<ResizeObserver | null>(null);
  // A ref callback, not an effect: the pane appears with the documents, long after this mounts.
  const attachStage = useCallback((node: HTMLDivElement | null): void => {
    watching.current?.disconnect();
    watching.current = null;
    if (!node) return;

    const measure = (): void => {
      const scale = node.clientWidth / REPORT_PAGE_WIDTH;
      const pitch = REPORT_ROW_PITCH * scale;
      const below = node.clientHeight - REPORT_FIRST_ROW * scale;
      setSliver(below > 0 ? below - Math.floor(below / pitch) * pitch : 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    watching.current = new ResizeObserver(measure);
    watching.current.observe(node);
  }, []);
  useEffect(() => () => watching.current?.disconnect(), []);

  const renderSent = (sent: IncidentReportSendResult): JSX.Element => (
    // Rises into place: the dialog has just changed what it is for, and a swap reads as a glitch.
    <div className="animate-in fade-in slide-in-from-bottom-3 py-2 duration-300 ease-out motion-reduce:animate-none">
      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
        <span
          className={cx(
            'flex size-6 shrink-0 items-center justify-center rounded-full',
            sent.dispatched ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Check className="size-3.5" aria-hidden />
        </span>
        {sent.dispatched ? t('incidentReports.sentTo', { count: sent.mails }) : t('incidentReports.mailDisabled')}
        {/* Said only when it happened: a count of people copied in on mails that never left would
                be two claims for the price of one. */}
        {sent.dispatched && sent.copied > 0 && (
          <span className="font-normal text-muted-foreground">
            {t('incidentReports.copiedIn', { count: sent.copied })}
          </span>
        )}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        {t('incidentReports.filesAttached', { count: sent.files.length })}
      </p>
      <ul className="mt-2 space-y-1">
        {sent.files.map((file, index) => (
          <li
            key={file}
            // One after another, in the order they went on the message.
            style={{ animationDelay: `${120 + index * 60}ms` }}
            className="animate-in fade-in slide-in-from-bottom-1 fill-mode-backwards flex items-center gap-2 text-sm text-foreground duration-200 motion-reduce:animate-none"
          >
            <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            {file}
          </li>
        ))}
      </ul>
    </div>
  );

  const renderCompose = (): JSX.Element => (
    // Laid out as a mail client lays a draft out, attachments above the body, because that is what is checked.
    <div
      className={cx(
        'flex h-full flex-col transition-opacity duration-200',
        send.isPending && 'pointer-events-none opacity-60',
      )}
    >
      {/* The mail fills the step: everything above it is as tall as it needs to be, and the frame
              has the rest. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card">
        {/*
              Who it goes to, on the mail rather than back with the pickers: the batch is settled by
              then, and this is the one choice that belongs to the message. Prefilled the same way as
              before — whoever the last send to this client went to, else their own contacts.

              The field holds the chips and takes anything typed; the picker beside it speaks for the
              client's contacts and the chosen products' managers.
            */}
        <div className="flex shrink-0 items-start gap-2 border-b border-border px-4 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <ApprovalRecipientsField
              ref={toField}
              label={<span className="sr-only">{t('to')}</span>}
              // Named in the box: a label to the left pushes the caret in from where the addresses sit.
              placeholder={t('incidentReports.recipients')}
              invalidEmailMessage={t('invalidEmailDraft')}
              value={toChips}
              onChange={next => {
                noteAdditions(next);
                setTo(next);
              }}
              onBlur={() => undefined}
              // The mail row is the box; a second one around the chips is a box in a box.
              bare
            />
          </div>
          <MultiSelectField
            label={<span className="sr-only">{t('incidentReports.recipients')}</span>}
            // A click inside the frame is invisible out here, so the frame tells the list to close.
            open={pickingRecipients}
            onOpenChange={setPickingRecipients}
            options={recipientOptions}
            value={chosenOffered}
            onChange={onContactsChange}
            emptyLabel={t('incidentReports.selectRecipients')}
            selectAllLabel={t('selectAll')}
            summaryThreshold={1}
            renderCountSummary={count => t('incidentReports.peopleSelected', { count })}
            searchable={recipientOptions.length > 6}
            className="w-40 shrink-0 gap-0"
            // Wider than its trigger, where every row read "Firs…" over "test…"; hung right, or it spills out of the dialog.
            contentClassName="w-80"
            align="end"
          />
        </div>

        {/* The send goes to the copied-in people rather than refusing, which is right for a trial run and
            wrong to leave unsaid. */}
        {noClientAddressed && (
          <p className="shrink-0 border-b border-border px-4 py-1.5 text-sm text-muted-foreground">
            {t('incidentReports.noClientAddressed')}
          </p>
        )}

        <div className="flex shrink-0 items-baseline border-b border-border px-4 py-1.5 text-sm">
          {/* Named in the box, the same as the row above it, and to a screen reader either way. */}
          <Label htmlFor="incident-mail-subject" className="sr-only">
            {t('incidentReports.subject')}
          </Label>
          {drafting && !subject ? (
            <Skeleton className="my-1 h-6 flex-1" />
          ) : (
            <Input
              id="incident-mail-subject"
              value={subject}
              onChange={event => setSubject(event.target.value)}
              placeholder={t('incidentReports.subject')}
              // The mail's own first line, indented by a chip's padding so everything starts on one vertical.
              className="h-7 rounded-none border-0 bg-transparent px-2.5 font-medium shadow-none focus-visible:ring-0 dark:bg-transparent"
            />
          )}
        </div>

        {/* Where a mail client puts them: above the message, so what is riding along is read before
                the words that introduce it. The strip from the step before, minus its controls — the
                batch was settled there, and these are the same documents under the same short names. */}
        <div className="flex shrink-0 flex-wrap gap-1 border-b border-border bg-muted/60 p-1.5">
          {files.map(file => (
            <span
              key={file.key}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-xs"
            >
              <FileText className="size-3.5 shrink-0" aria-hidden />
              <span className="max-w-56 truncate">{tabLabel(file)}</span>
            </span>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {draft ? (
            <MailPreview
              html={draft.html}
              message={message}
              draftMessage={draft.message}
              names={greetedNames}
              onChange={setMessage}
              onPress={() => setPickingRecipients(false)}
            />
          ) : (
            /* Holds the frame's place whether or not the draft has been asked for yet, so arriving
                   at the step is not also a resize. */
            <Skeleton className="min-h-0 w-full flex-1 rounded-none" />
          )}
        </div>
      </div>
    </div>
  );

  const renderChoose = (): JSX.Element => (
    // Dimmed while the send is in flight, so the pending button is not the only sign of it.
    <div
      className={cx(
        'flex h-full flex-col gap-4 transition-opacity duration-200',
        send.isPending && 'pointer-events-none opacity-60',
      )}
    >
      {/* The choices, in the order they depend on each other: a client decides which sprints have
            support work, and the chosen sprint decides which products have anything in it. */}
      <div className="grid shrink-0 gap-3 sm:grid-cols-3">
        <MultiSelectField
          label={t('incidentReports.client')}
          options={clientOptions}
          value={chosenClient ? [chosenClient] : []}
          onChange={next => {
            setChosenClient(next[0]);
            setActiveFile(null);
          }}
          open={openPicker === 'client'}
          onOpenChange={next => setOpenPicker(next ? 'client' : null)}
          emptyLabel={t('incidentReports.selectClient')}
          searchPlaceholder={t('search')}
          emptySearchLabel={t('noMatches')}
          single
        />
        <MultiSelectField
          label={t('sprints')}
          options={sprintOptions}
          value={chosenSprints}
          onChange={next => {
            setChosenSprints(next);
            setActiveFile(null);
          }}
          open={openPicker === 'sprints'}
          onOpenChange={next => setOpenPicker(next ? 'sprints' : null)}
          emptyLabel={nothingToReport ? t('incidentReports.noSprintsToReport') : t('incidentReports.selectSprints')}
          summaryThreshold={1}
          renderCountSummary={count => t('incidentReports.sprintsSelected', { count })}
          searchPlaceholder={t('search')}
          emptySearchLabel={t('noMatches')}
          disabled={!company}
        />
        <MultiSelectField
          label={t('titles.products')}
          options={productOptions}
          value={chosenProducts}
          onChange={setChosenProducts}
          open={openPicker === 'products'}
          onOpenChange={next => setOpenPicker(next ? 'products' : null)}
          emptyLabel={t('incidentReports.selectProducts')}
          selectAllLabel={t('incidentReports.allProducts')}
          summaryThreshold={1}
          renderCountSummary={count => t('incidentReports.productsSelected', { count })}
          searchable={false}
          disabled={!company}
        />
      </div>

      {/*
          The preview, on a stage that never changes size.

          The same shape the Insights export and the sprint export dialog use: a dashed placeholder
          fills the space until there is something to show, and the window comes up over it. The stage
          is one size throughout — the box you look at while choosing is the box the document arrives
          in — because a PDF viewer re-fits its page every time it is handed a new size, and doing that
          on every frame of an animation both stutters and shows the viewer's own dark chrome while it
          catches up. So the window arrives on opacity and a few pixels of travel, which cost no layout
          at all, rather than on a height that grows into place.
        */}
      <div className="relative min-h-0 flex-1">
        <div
          className={cx(
            'absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-6 text-center transition-opacity duration-300',
            hasFiles && 'pointer-events-none opacity-0',
          )}
        >
          <FileText className="size-8 text-muted-foreground/50" aria-hidden />
          <p className="text-sm text-muted-foreground">{emptyPreview}</p>
        </div>

        {/* Open as soon as there is a batch, rather than once its documents have been drawn: the tabs
            are the batch, and they are known the moment the pickers are answered. What waits for the
            documents is the pane under them, which says so itself. */}
        <div
          className={cx(
            'absolute inset-0 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
            hasFiles ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
          )}
        >
          <div className="h-full">
            {/*
                One window, and a tab in it per file — because that is what these are: separate PDFs,
                each one an attachment. Firefox's shape rather than Chrome's: a floating rounded tab
                sits on the strip instead of merging into the toolbar below it, which is both nicer and
                honest about these being documents rather than pages of one.

                No close control on a tab; the pickers above decide what is in the batch.
              */}
            <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border">
              <div className="flex shrink-0 gap-1 overflow-x-auto bg-muted/60 p-1.5">
                {files.map((file, index) => (
                  <div
                    key={file.key}
                    // Tabs arrive left to right, which reads as the batch being assembled.
                    style={{ animationDelay: `${index * 45}ms` }}
                    className={cx(
                      'flex shrink-0 items-center gap-1 rounded-lg pl-3 pr-1 text-xs transition-colors',
                      'animate-in fade-in slide-in-from-left-2 fill-mode-backwards duration-200 motion-reduce:animate-none',
                      file.key === active?.key
                        ? 'bg-card font-medium text-foreground shadow-xs'
                        : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFile(file.key)}
                      className="flex min-w-0 items-center gap-1.5 py-1.5"
                    >
                      <FileText className="size-3.5 shrink-0" aria-hidden />
                      <span className="max-w-56 truncate">{tabLabel(file)}</span>
                    </button>
                    {/* Reading a report is when what is wrong with it shows up, so the correction is
                          on the tab rather than somewhere else in the app. A sibling of the tab's own
                          button: a button cannot hold another one. */}
                    <button
                      type="button"
                      aria-label={`${t('incidentReports.editReported')} - ${tabLabel(file)}`}
                      onClick={() => {
                        setActiveFile(file.key);
                        setEditing(file.key);
                      }}
                      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Pencil className="size-3.5" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>

              <div ref={attachStage} className="relative min-h-0 flex-1 bg-muted/30">
                {files.map(file => (
                  <ReportFrame
                    key={file.key}
                    productId={file.productId}
                    sprintId={file.sprintId}
                    filename={filenameOf(file)}
                    active={file.key === active?.key}
                    clickThrough={!!openPicker}
                    onReady={() => setReadyFiles(current => new Set(current).add(file.key))}
                  />
                ))}
                {/* Whatever the pane's own bottom edge would slice off the row it lands in. A PDF
                      viewer has no document to read from out here, so the page is met on its own grid
                      instead: the pane ends on a row's line, and the part-row below it is the page's
                      own white rather than half a line of text. */}
                {showWindow && (
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-white"
                    style={{ height: sliver }}
                  />
                )}

                {/* The wait, said where the document will be rather than instead of the window, and
                    faded out as that document comes up so the two are never both there to read. */}
                <div
                  className={cx(
                    'pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-center transition-opacity duration-200',
                    showWindow && 'opacity-0',
                  )}
                >
                  <FileText className="size-8 text-muted-foreground/50" aria-hidden />
                  <p className="text-sm text-muted-foreground">{t('incidentReports.checking')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {company ? t('incidentReports.exportTitle', { client: company.name }) : t('incidentReports.exportAction')}
          </DialogTitle>
        </DialogHeader>

        {/* One height for both steps — the taller one's, which is fixed by the placeholder that holds
            the preview's space — so stepping between them moves the content and never the window. It
            gives way only on a window with no room for it, rather than pushing the dialog off the top
            of the screen.

            The steps stand one on top of the other rather than replacing each other: the one being
            left keeps its place, hidden, because its documents are loaded and going back to a window
            that fetches them again is going back to a different window. The mail is the exception —
            it is mounted on arrival, which is what plays its greeting. */}
        {result ? (
          renderSent(result)
        ) : (
          <div className="relative h-[min(588px,calc(100vh-176px))] overflow-hidden">
            {/* The onboarding dialog's own transition: the step being left is simply not there any
                more, and the one arriving slides in from the right as it comes.

                This one is not unmounted, only hidden outright — its documents are loaded, and fetching
                them again on the way back is a different window coming back. Hidden rather than faded
                for the same reason the onboarding dialog unmounts: a page you can see through is a page
                you are still looking at, and this one carries a black banner.

                It takes 400ms rather than that dialog's 300ms. The travel is the same share of the
                width in both — the utility moves an element by a percentage of itself — but this window
                is half again as wide, so the same share is half again the distance, and covering it in
                the same time is moving that much faster. The extra 100ms is what keeps the speed. */}
            <div
              aria-hidden={step !== 'choose'}
              className={cx(
                'absolute inset-0 px-0.5',
                step === 'choose'
                  ? moved &&
                      'duration-[400ms] ease-out animate-in fade-in-0 slide-in-from-right-6 motion-reduce:animate-none'
                  : 'invisible pointer-events-none',
              )}
            >
              {renderChoose()}
            </div>

            {step === 'compose' && (
              <div className="absolute inset-0 px-0.5 duration-[400ms] ease-out animate-in fade-in-0 slide-in-from-right-6 motion-reduce:animate-none">
                {renderCompose()}
              </div>
            )}

            {/* Over both steps rather than beside them, and opaque: what is under it is kept mounted
                so its documents are still loaded when the panel goes, but a page you can see through
                is a page you are still reading. It arrives from below — the two steps travel sideways,
                and a detour that came in the same direction would read as a third one. */}
            {editing && (
              <div
                className={cx(
                  'absolute inset-0 z-10 bg-popover px-0.5 duration-300 ease-out motion-reduce:animate-none',
                  leavingEntries
                    ? 'pointer-events-none animate-out fade-out-0 slide-out-to-bottom-4 fill-mode-forwards'
                    : 'animate-in fade-in-0 slide-in-from-bottom-4',
                )}
              >
                <IncidentReportEntriesPanel
                  organizationId={organizationId}
                  files={entriesFiles}
                  activeKey={editing}
                  onActiveKeyChange={key => {
                    setEditing(key);
                    // The preview goes with it, so leaving the panel lands on the document just read.
                    setActiveFile(key);
                  }}
                  onBack={closeEntries}
                  // Its own document is prepared again; the rest of the batch was not touched.
                  onCorrected={file => refreshReport(file.productId, file.sprintId)}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result && <Button onClick={() => onOpenChange(false)}>{t('actions.close')}</Button>}

          {/* The send's own buttons are the panel's to hide: nothing is sent from a detour, and Next
              from here would step over the correction being read. */}
          {!result && editing && <Button onClick={closeEntries}>{t('incidentReports.backToReport')}</Button>}

          {!result && !editing && step === 'choose' && (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              {/* Nothing is sent from this step: it hands over a batch, and the next one is where the
                  mail is read and sent. It waits here for the draft rather than stepping over to a
                  mail that is not written yet. */}
              <Button
                onClick={() => (draft ? goTo('compose') : setAwaited(true))}
                disabled={files.length === 0 || awaited}
              >
                {awaited && <Loader absolute={false} size={LoaderSize.small} className="mr-1.5" />}
                {t('actions.next')}
              </Button>
            </>
          )}

          {!result && !editing && step === 'compose' && (
            <>
              <Button variant="ghost" onClick={() => goTo('choose')} disabled={send.isPending}>
                {t('actions.back')}
              </Button>
              <Button onClick={submit} disabled={recipients.length === 0 || files.length === 0 || send.isPending}>
                {send.isPending && <Loader absolute={false} size={LoaderSize.small} className="mr-1.5" />}
                {send.isPending
                  ? t('incidentReports.sending')
                  : t('incidentReports.sendCount', { count: files.length })}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncidentReportExportDialog;
